// react
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { createFileRoute, useNavigate } from '@tanstack/react-router';

// material ui icons
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import CloudRoundedIcon from '@mui/icons-material/CloudRounded';
import EnergySavingsLeafRoundedIcon from '@mui/icons-material/EnergySavingsLeafRounded';
import HomeWorkRoundedIcon from '@mui/icons-material/HomeWorkRounded';
import LocalFireDepartmentRoundedIcon from '@mui/icons-material/LocalFireDepartmentRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import PercentRoundedIcon from '@mui/icons-material/PercentRounded';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';

// material ui
import {
  alpha,
  Avatar,
  capitalize,
  Card,
  CardContent,
  CardMedia,
  Grid,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';

// api
import { useGetExtractCachedEnergySavingQuery } from '@/api/energy-savings/extractCachedEnergySavingsApiSlice';
import { useGetBuildingsByAccountQuery } from '@/api/recalc/endpoints/buildingApiSlice';
import {
  useGetBaselineDaysByJobSubscriptionIdQuery,
  useGetSubscriptionsByAccountQuery,
} from '@/api/recalc/endpoints/jobSubscriptionApiSlice';

// shared components
import CardHeaderWithIcon from '@/components/shared/surfaces/card/CardHeaderWithIcon';
import GatewayLoadingIndicator from '@/components/ui/progress/GatewayLoadingIndicator';

// feature-utils
import initializeTimeRange from '@/features/building-optimization/utils/helpers';

// components
import IMG from '@/features/building-optimization/assets/Predictive-Heat-Optimization.webp';
import { useAlertProvider } from '@/hooks/useAlertProvider';
import { getSelectedAccount } from '@/redux/selectedAccountSlice';
import ErrorBoundary from '@/routes/-ErrorBoundary';

import { DateTime } from 'luxon';

const formatEnergyUnit = (value) => {
  if (Math.abs(value) >= 1000000) {
    return (value / 1000000)?.toFixed(1) + ' GWh';
  } else if (Math.abs(value) >= 1000) {
    return (value / 1000)?.toFixed(1) + ' MWh';
  } else {
    return value.toFixed(0) + ' kWh';
  }
};

function formatAndEncodeTimestampForAPI(date) {
  const isoString = DateTime.fromJSDate(date, { zone: 'utc' }).toFormat(
    "yyyy-MM-dd'T'HH:mm:ss'Z'",
  );
  return encodeURIComponent(isoString);
}

export const Route = createFileRoute(
  '/$accountId/_user/predictive-heat-control/',
)({
  component: RouteComponent,
  errorComponent: ErrorBoundary,
});

function RouteComponent() {
  const { t } = useTranslation('buildingOptimization');
  const account = useSelector(getSelectedAccount);
  const navigate = useNavigate();
  const { start, end, isTodayInSeason, daysUntilNextSeason } =
    initializeTimeRange();

  const { data: buildings } = useGetBuildingsByAccountQuery(
    account?.AccountId,
    {
      skip: !account?.AccountId,
    },
  );

  const { data: subscriptions } = useGetSubscriptionsByAccountQuery(
    account?.AccountId,
    {
      skip: !account?.AccountId,
    },
  );

  const predictiveSub = subscriptions?.find(
    (sub) => sub.JobType === 'PredictiveHeatControl',
  );

  const { data: baselineDays } = useGetBaselineDaysByJobSubscriptionIdQuery(
    {
      jobSubscriptionId: predictiveSub?.JobSubscriptionId,
      timestamp: {
        startDate: formatAndEncodeTimestampForAPI(new Date(start)),
        endDate: formatAndEncodeTimestampForAPI(new Date(end)),
      },
    },
    { skip: !predictiveSub?.JobSubscriptionId },
  );

  const buildingIds = useMemo(
    () => (buildings ? buildings.map((b) => b.BuildingId) : []),
    [buildings],
  );

  const {
    data: energySavings,
    isLoading: isLoadingEnergySavings,
    isFetching: isFetchingEnergySavings,
  } = useGetExtractCachedEnergySavingQuery(
    { buildingsIdArray: buildingIds },
    { skip: !account?.AccountId || buildingIds.length === 0 },
  );

  const {
    Heat,
    HeatCO2Savings,
    Gas,
    GasCO2Savings,
    Electricity,
    ElectricityCO2Savings,
    BuildingEnergySavings,
    EnergySavingsSummary,
    CO2SavingsSummary,
  } = energySavings || {};

  const buildingsEnrichedWithSavings = useMemo(() => {
    if (!buildings || !energySavings) return [];
    return buildings.map((building) => {
      const savings = BuildingEnergySavings.find(
        (s) => s.BuildingId === building.BuildingId,
      );
      return {
        ...building,
        ...savings,
      };
    });
  }, [buildings, energySavings, BuildingEnergySavings]);

  const handleBuildingClick = useCallback(
    (building) => () => {
      navigate({
        to: `${encodeURIComponent(building.Name)}`,
        state: {
          buildingId: building.BuildingId,
          enrichedBuilding: building,
        } as never,
      });
    },
    [navigate],
  );

  const hasAnySupply =
    Heat?.TotalEnergyUsage != null ||
    Electricity?.TotalEnergyUsage != null ||
    Gas?.TotalEnergyUsage != null;

  const hasNoBuildings =
    buildingsEnrichedWithSavings.length === 0 &&
    !isLoadingEnergySavings &&
    !isFetchingEnergySavings;

  const hasNoSourcesOfSupply =
    !Heat?.TotalEnergyUsage &&
    !Electricity?.TotalEnergyUsage &&
    !Gas?.TotalEnergyUsage &&
    !isLoadingEnergySavings &&
    !isFetchingEnergySavings;

  useAlertProvider({
    enabled: hasNoBuildings,
    severity: 'warning',
    message: t('building.error.notFound', {
      ns: 'buildingOptimization',
    }),
  });

  useAlertProvider({
    // Only shown when buildings exist but none of them report supply data,
    // since the "no buildings" alert above takes priority.
    enabled: hasNoSourcesOfSupply && !hasNoBuildings,
    severity: 'warning',
    message: t('sourcesOfSupply.error.notFound', {
      ns: 'buildingOptimization',
    }),
  });

  if (hasNoBuildings) {
    return null;
  }

  return (
    <>
      <GatewayLoadingIndicator
        isLoading={isLoadingEnergySavings || isFetchingEnergySavings}
      />
      <Stack mb={2}>
        <Typography variant="h5" fontWeight={600}>
          {t('predictiveHeatControlSavingsDashboard.title', {
            ns: 'buildingOptimization',
          })}
        </Typography>
      </Stack>
      {isTodayInSeason && baselineDays && baselineDays.length < 10 && (
        <Card sx={{ mb: 2 }}>
          <CardMedia
            component="img"
            height="200"
            image={IMG}
            alt="green iguana"
            sx={{
              objectFit: 'cover',
              objectPosition: 'top',
            }}
          />
          <CardContent>
            <Typography gutterBottom variant="h5" component="div">
              {t('predictiveHeatControlSavingsDashboard.season.started', {
                ns: 'buildingOptimization',
              })}
              {' - '}
              {t(
                'predictiveHeatControlSavingsDashboard.season.moreDataNeeded',
                {
                  ns: 'buildingOptimization',
                },
              )}
            </Typography>
            <Typography variant="body1">
              {t(
                'predictiveHeatControlSavingsDashboard.notEnoughBaselineDays',
                {
                  count: baselineDays?.length,
                  minCount: 10,
                  ns: 'buildingOptimization',
                },
              )}{' '}
              {t('predictiveHeatControlSavingsDashboard.nextSeasonStart', {
                count: daysUntilNextSeason,
                ns: 'buildingOptimization',
              })}
            </Typography>
          </CardContent>
        </Card>
      )}
      <Grid container spacing={2} mb={4}>
        <Grid
          size={{
            xs: 12,
            md: 6,
            lg: 2.4
          }}>
          <Card sx={{ padding: 2, height: '100%' }}>
            <CardHeaderWithIcon
              icon={<SpeedRoundedIcon />}
              tooltip={
                t?.('dashboardCards.totalConsumption.tooltip') ||
                'Totalforbruget er det aflæste forbrug direkte fra forsyningsselskabet, afregningsmåleren eller hovedmåleren.'
              }
              title={
                t?.('dashboardCards.totalConsumption.title') || 'Samlet Forbrug'
              }
            />
            <Stack
              direction="row"
              alignItems="center"
              justifyContent={'space-between'}
            >
              {isLoadingEnergySavings || isFetchingEnergySavings ? (
                <Skeleton variant="text" width="100%" height={40} />
              ) : (
                <Typography variant="h5">
                  {EnergySavingsSummary?.TotalEnergyUsage >= 1000000 ||
                  EnergySavingsSummary?.TotalEnergyUsage <= -1000000
                    ? (
                        EnergySavingsSummary?.TotalEnergyUsage / 1000000
                      ).toFixed(1) + ' GWh'
                    : EnergySavingsSummary?.TotalEnergyUsage >= 1000 ||
                        EnergySavingsSummary?.TotalEnergyUsage <= -1000
                      ? (EnergySavingsSummary?.TotalEnergyUsage / 1000).toFixed(
                          1,
                        ) + ' MWh'
                      : EnergySavingsSummary?.TotalEnergyUsage.toFixed(0) +
                        ' kWh'}
                </Typography>
              )}
            </Stack>
          </Card>
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 6,
            lg: 2.4
          }}>
          <Card sx={{ padding: 2, height: '100%' }}>
            <CardHeaderWithIcon
              icon={<EnergySavingsLeafRoundedIcon />}
              tooltip={
                t?.('dashboardCards.totalEnergySavings.tooltip') ||
                'Den samlede besparelse er forskellen mellem det beregnede forbrug uden Prædiktiv Varmeoptimering og det aflæste forbrug i perioden. Det beregnede forbrug uden Prædiktiv Varmeoptimering for hver bygning kan findes ved at klikke ind på pågældende bygning.'
              }
              title={
                t?.('dashboardCards.totalEnergySavings.title') ||
                'Samlet energibesparelse'
              }
            />
            <Stack
              direction="row"
              alignItems="center"
              justifyContent={'space-between'}
            >
              {isLoadingEnergySavings || isFetchingEnergySavings ? (
                <Skeleton variant="text" width="100%" height={40} />
              ) : (
                <Typography variant="h5">
                  {EnergySavingsSummary?.TotalEnergySavings >= 1000000 ||
                  EnergySavingsSummary?.TotalEnergySavings <= -1000000
                    ? (
                        EnergySavingsSummary?.TotalEnergySavings / 1000000
                      ).toFixed(1) + ' GWh'
                    : EnergySavingsSummary?.TotalEnergySavings >= 1000 ||
                        EnergySavingsSummary?.TotalEnergySavings <= -1000
                      ? (
                          EnergySavingsSummary?.TotalEnergySavings / 1000
                        ).toFixed(1) + ' MWh'
                      : EnergySavingsSummary?.TotalEnergySavings.toFixed(0) +
                        ' kWh'}
                </Typography>
              )}
            </Stack>
          </Card>
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6,
            lg: 2.4
          }}>
          <Card sx={{ padding: 2, height: '100%' }}>
            <CardHeaderWithIcon
              icon={<PercentRoundedIcon />}
              tooltip={
                t?.('dashboardCards.percentageSavings.tooltip') ||
                'Besparelsesprocenten er baseret på forholdet mellem gennemsnitsforbruget på dage uden optimering (baselinedage) og det faktiske forbrug. Der bruges her et vægtet gennemsnit på tværs af alle bygninger tilknyttet denne konto.'
              }
              title={
                t?.('dashboardCards.percentageSavings.title') ||
                'Procentvis besparelse'
              }
            />
            <Stack
              direction="row"
              alignItems="center"
              justifyContent={'space-between'}
            >
              {isLoadingEnergySavings || isFetchingEnergySavings ? (
                <Skeleton variant="text" width="100%" height={40} />
              ) : (
                <Typography variant="h5">
                  {EnergySavingsSummary?.TotalEnergySavingsPercentage.toFixed(
                    1,
                  ) + ' %' || '—'}
                </Typography>
              )}
            </Stack>
          </Card>
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6,
            lg: 2.4
          }}>
          <Card sx={{ padding: 2, height: '100%' }}>
            <CardHeaderWithIcon
              icon={<CloudRoundedIcon />}
              tooltip={
                t?.('dashboardCards.totalCO2Savings.tooltip') ||
                'Besparelsen er forholdet mellem forbruget, hvis alle dage var uden optimering (baselinedage) set i forhold til det faktiske forbrug.'
              }
              title={
                t?.('dashboardCards.totalCO2Savings.title') ||
                'Samlet CO₂ besparelse'
              }
            />
            <Stack
              direction="row"
              alignItems="center"
              justifyContent={'space-between'}
            >
              {isLoadingEnergySavings || isFetchingEnergySavings ? (
                <Skeleton variant="text" width="100%" height={40} />
              ) : (
                <Typography variant="h5">
                  {CO2SavingsSummary?.TotalCO2Savings >= 1000000 ||
                  CO2SavingsSummary?.TotalCO2Savings <= -1000000
                    ? (CO2SavingsSummary?.TotalCO2Savings / 1000000).toFixed(
                        1,
                      ) + t('tco2e', { ns: 'datatypes' })
                    : CO2SavingsSummary?.TotalCO2Savings >= 1000 ||
                        CO2SavingsSummary?.TotalCO2Savings <= -1000
                      ? (CO2SavingsSummary?.TotalCO2Savings / 1000).toFixed(1) +
                        t('kgco2e', { ns: 'datatypes' })
                      : CO2SavingsSummary?.TotalCO2Savings.toFixed(0) +
                        t('gco2e', { ns: 'datatypes' })}
                </Typography>
              )}
            </Stack>
          </Card>
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 12,
            lg: 2.4
          }}>
          <Card
            onClick={() =>
              navigate({
                to: 'calendar',
                replace: true,
              })
            }
            sx={{
              display: 'flex',
              backgroundColor: (theme) =>
                theme.palette.mode === 'light' ? '#f5f5f5' : '#303030',
              height: '100%',
              cursor: 'pointer',
              color: (theme) => theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: (theme) => theme.palette.action.hover,
                color: (theme) => theme.palette.text.primary,
              },
              transition: 'background-color 0.3s, color 0.3s, box-shadow 0.3s',
            }}
          >
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                <Avatar
                  variant="rounded"
                  sx={{
                    bgcolor: (theme) => alpha(theme.palette.primary.light, 0.1),
                    mr: 1,
                    borderRadius: 3,
                  }}
                >
                  <CalendarMonthRoundedIcon
                    sx={{ verticalAlign: 'middle', color: 'primary.main' }}
                  />
                </Avatar>
                {t('card.calendar.title', { ns: 'buildingOptimization' })}
              </Typography>
              <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                {t('card.calendar.description', {
                  ns: 'buildingOptimization',
                })}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Stack mb={2}>
        <Typography variant="h5" fontWeight={600} mb={1}>
          {t('sourcesOfSupply.title', {
            ns: 'buildingOptimization',
          })}
        </Typography>
        {Heat?.TotalEnergyUsage !== 0 && (
          <ListItem
            sx={{
              mb: 1,
              borderRadius: 2,
              // border: '1px solid',
              // borderColor: 'divider',
              backgroundColor: (theme) =>
                theme.palette.mode === 'light'
                  ? theme.palette.background.paper
                  : theme.palette.grey[800],
            }}
          >
            <ListItemIcon>
              {isLoadingEnergySavings || isFetchingEnergySavings ? (
                <Skeleton
                  variant="rectangular"
                  width={40}
                  height={40}
                  sx={{ borderRadius: 3 }}
                />
              ) : (
                <Avatar
                  variant="rounded"
                  sx={{
                    bgcolor: alpha('#c2dd00', 0.1),
                    mr: 1,
                    borderRadius: 3,
                  }}
                >
                  <MenuRoundedIcon
                    sx={{
                      verticalAlign: 'middle',
                      color: '#96b100',
                      transform: 'rotate(90deg)',
                    }}
                  />
                </Avatar>
              )}
            </ListItemIcon>
            <ListItemText
              primary="Type"
              secondary={
                isLoadingEnergySavings || isFetchingEnergySavings ? (
                  <Skeleton variant="text" width={80} height={20} />
                ) : (
                  'Fjernvarme'
                )
              }
              sx={{ flex: 2 }}
              slotProps={{
                primary: {
                  variant: 'subtitle2',
                  color: 'textSecondary',
                },

                secondary: {
                  variant: 'subtitle1',
                  color: 'textPrimary',
                }
              }} />
            <ListItemText
              primary="Samlet aflæst forbrug"
              secondary={
                isLoadingEnergySavings || isFetchingEnergySavings ? (
                  <Skeleton variant="text" width={80} height={20} />
                ) : Heat?.BaseUnit === 'kWh' ? (
                  formatEnergyUnit(Heat?.TotalEnergyUsage)
                ) : (
                  `${Heat?.TotalEnergyUsage.toFixed(1)} ${Heat?.BaseUnit}`
                )
              }
              slotProps={{
                primary: {
                  variant: 'subtitle2',
                  color: 'textSecondary',
                },

                secondary: {
                  variant: 'subtitle1',
                  color: 'textPrimary',
                }
              }} />

            <ListItemText
              primary="Effektiv besparelse"
              secondary={
                isLoadingEnergySavings || isFetchingEnergySavings ? (
                  <Skeleton variant="text" width={80} height={20} />
                ) : Heat?.BaseUnit === 'kWh' ? (
                  formatEnergyUnit(Heat?.TotalEnergySavings)
                ) : (
                  `${Heat?.TotalEnergySavings.toFixed(1)} ${Heat?.BaseUnit}`
                )
              }
              slotProps={{
                primary: {
                  variant: 'subtitle2',
                  color: 'textSecondary',
                },

                secondary: {
                  variant: 'subtitle1',
                  color: 'textPrimary',
                }
              }} />

            <ListItemText
              primary="CO2 Besparelse"
              secondary={
                isLoadingEnergySavings || isFetchingEnergySavings ? (
                  <Skeleton variant="text" width={120} height={20} />
                ) : (
                  HeatCO2Savings?.TotalCO2Savings !== undefined &&
                  (Math.abs(HeatCO2Savings.TotalCO2Savings) >= 1000000
                    ? `${(HeatCO2Savings.TotalCO2Savings / 1000000).toFixed(1)} t CO2e`
                    : Math.abs(HeatCO2Savings.TotalCO2Savings) >= 1000
                      ? `${(HeatCO2Savings.TotalCO2Savings / 1000).toFixed(1)} kg CO2e`
                      : `${HeatCO2Savings.TotalCO2Savings.toFixed(0)} g CO2e`)
                )
              }
              slotProps={{
                primary: {
                  variant: 'subtitle2',
                  color: 'textSecondary',
                },

                secondary: {
                  variant: 'subtitle1',
                  color: 'textPrimary',
                }
              }} />
          </ListItem>
        )}

        {Electricity?.TotalEnergyUsage !== 0 && (
          <ListItem
            sx={{
              backgroundColor: 'background.paper',
              mb: 1,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <ListItemIcon>
              {isLoadingEnergySavings || isFetchingEnergySavings ? (
                <Skeleton
                  variant="rectangular"
                  width={40}
                  height={40}
                  sx={{ borderRadius: 3 }}
                />
              ) : (
                <Avatar
                  variant="rounded"
                  sx={{
                    bgcolor: alpha('#c2dd00', 0.1),
                    mr: 1,
                    borderRadius: 3,
                  }}
                >
                  <BoltRoundedIcon
                    sx={{
                      verticalAlign: 'middle',
                      color: '#96b100',
                    }}
                  />
                </Avatar>
              )}
            </ListItemIcon>
            <ListItemText
              primary="Type"
              secondary={
                isLoadingEnergySavings || isFetchingEnergySavings ? (
                  <Skeleton variant="text" width={80} height={20} />
                ) : (
                  'El'
                )
              }
              sx={{ flex: 2 }}
              slotProps={{
                primary: {
                  variant: 'subtitle2',
                  color: 'textSecondary',
                },

                secondary: {
                  variant: 'subtitle1',
                  color: 'textPrimary',
                }
              }} />
            <ListItemText
              primary="Samlet aflæst forbrug"
              secondary={
                isLoadingEnergySavings || isFetchingEnergySavings ? (
                  <Skeleton variant="text" width={80} height={20} />
                ) : Electricity?.BaseUnit === 'kWh' ? (
                  formatEnergyUnit(Electricity?.TotalEnergyUsage)
                ) : (
                  `${Electricity?.TotalEnergyUsage.toFixed(1)} ${Electricity?.BaseUnit}`
                )
              }
              slotProps={{
                primary: {
                  variant: 'subtitle2',
                  color: 'textSecondary',
                },

                secondary: {
                  variant: 'subtitle1',
                  color: 'textPrimary',
                }
              }} />

            <ListItemText
              primary="Effektiv besparelse"
              secondary={
                isLoadingEnergySavings || isFetchingEnergySavings ? (
                  <Skeleton variant="text" width={80} height={20} />
                ) : Electricity?.BaseUnit === 'kWh' ? (
                  formatEnergyUnit(Electricity?.TotalEnergySavings)
                ) : (
                  `${Electricity?.TotalEnergySavings.toFixed(1)} ${Electricity?.BaseUnit}`
                )
              }
              slotProps={{
                primary: {
                  variant: 'subtitle2',
                  color: 'textSecondary',
                },

                secondary: {
                  variant: 'subtitle1',
                  color: 'textPrimary',
                }
              }} />

            <ListItemText
              primary="CO2 Besparelse"
              secondary={
                isLoadingEnergySavings || isFetchingEnergySavings ? (
                  <Skeleton variant="text" width={80} height={20} />
                ) : (
                  ElectricityCO2Savings?.TotalCO2Savings !== undefined &&
                  (Math.abs(ElectricityCO2Savings.TotalCO2Savings) >= 1000000
                    ? `${(ElectricityCO2Savings.TotalCO2Savings / 1000000).toFixed(1)} t CO2e`
                    : Math.abs(ElectricityCO2Savings.TotalCO2Savings) >= 1000
                      ? `${(ElectricityCO2Savings.TotalCO2Savings / 1000).toFixed(1)} kg CO2e`
                      : `${ElectricityCO2Savings.TotalCO2Savings.toFixed(0)} g CO2e`)
                )
              }
              slotProps={{
                primary: {
                  variant: 'subtitle2',
                  color: 'textSecondary',
                },

                secondary: {
                  variant: 'subtitle1',
                  color: 'textPrimary',
                }
              }} />
          </ListItem>
        )}

        {Gas?.TotalEnergyUsage !== 0 && (
          <ListItem
            sx={{
              backgroundColor: 'background.paper',
              mb: 1,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <ListItemIcon>
              {isLoadingEnergySavings || isFetchingEnergySavings ? (
                <Skeleton
                  variant="rectangular"
                  width={40}
                  height={40}
                  sx={{ borderRadius: 3 }}
                />
              ) : (
                <Avatar
                  variant="rounded"
                  sx={{
                    bgcolor: alpha('#c2dd00', 0.1),
                    mr: 1,
                    borderRadius: 3,
                  }}
                >
                  <LocalFireDepartmentRoundedIcon
                    sx={{
                      verticalAlign: 'middle',
                      color: '#96b100',
                    }}
                  />
                </Avatar>
              )}
            </ListItemIcon>
            <ListItemText
              primary="Type"
              secondary={
                isLoadingEnergySavings || isFetchingEnergySavings ? (
                  <Skeleton variant="text" width={80} height={20} />
                ) : (
                  'Gas'
                )
              }
              sx={{ flex: 2 }}
              slotProps={{
                primary: {
                  variant: 'subtitle2',
                  color: 'textSecondary',
                },

                secondary: {
                  variant: 'subtitle1',
                  color: 'textPrimary',
                }
              }} />

            <ListItemText
              primary="Samlet aflæst forbrug"
              secondary={
                isLoadingEnergySavings || isFetchingEnergySavings ? (
                  <Skeleton variant="text" width={80} height={20} />
                ) : Gas?.BaseUnit === 'kWh' ? (
                  formatEnergyUnit(Gas?.TotalEnergyUsage)
                ) : (
                  `${Gas?.TotalEnergyUsage.toFixed(1)} ${Gas?.BaseUnit}`
                )
              }
              slotProps={{
                primary: {
                  variant: 'subtitle2',
                  color: 'textSecondary',
                },

                secondary: {
                  variant: 'subtitle1',
                  color: 'textPrimary',
                }
              }} />

            <ListItemText
              primary="Effektiv besparelse"
              secondary={
                isLoadingEnergySavings || isFetchingEnergySavings ? (
                  <Skeleton variant="text" width={80} height={20} />
                ) : Gas?.BaseUnit === 'kWh' ? (
                  formatEnergyUnit(Gas?.TotalEnergySavings)
                ) : (
                  `${Gas?.TotalEnergySavings.toFixed(1)} ${Gas?.BaseUnit}`
                )
              }
              slotProps={{
                primary: {
                  variant: 'subtitle2',
                  color: 'textSecondary',
                },

                secondary: {
                  variant: 'subtitle1',
                  color: 'textPrimary',
                }
              }} />

            <ListItemText
              primary="CO2 Besparelse"
              secondary={
                isLoadingEnergySavings || isFetchingEnergySavings ? (
                  <Skeleton variant="text" width={80} height={20} />
                ) : (
                  GasCO2Savings?.TotalCO2Savings !== undefined &&
                  (Math.abs(GasCO2Savings.TotalCO2Savings) >= 1000000
                    ? `${(GasCO2Savings.TotalCO2Savings / 1000000).toFixed(1)} t CO2e`
                    : Math.abs(GasCO2Savings.TotalCO2Savings) >= 1000
                      ? `${(GasCO2Savings.TotalCO2Savings / 1000).toFixed(1)} kg CO2e`
                      : `${GasCO2Savings.TotalCO2Savings.toFixed(0)} g CO2e`)
                )
              }
              slotProps={{
                primary: {
                  variant: 'subtitle2',
                  color: 'textSecondary',
                },

                secondary: {
                  variant: 'subtitle1',
                  color: 'textPrimary',
                }
              }} />
          </ListItem>
        )}
      </Stack>

      <Stack mb={2}>
        <Typography variant="h5" fontWeight={600} mb={1}>
          {capitalize(t('items.building.indefinitePlural', { ns: 'glossary' }))}
        </Typography>

        {buildingsEnrichedWithSavings.map((building) => (
          <ListItemButton
            key={building.BuildingId}
            onClick={handleBuildingClick(building)}
            disabled={building.BillingMeters.length === 0 || !hasAnySupply}
            sx={{
              backgroundColor: (theme) =>
                theme.palette.mode === 'light'
                  ? theme.palette.background.paper
                  : theme.palette.grey[800],
              mb: 1,
              borderRadius: 2,
              // border: '1px solid',
              // borderColor: 'divider',

              // when hovering the whole row
              '&:hover .arrow-icon': {
                transform: 'rotate(180deg) translateX(-4px)',
                color: 'primary.main',
              },
            }}
          >
            <ListItemIcon>
              <Avatar
                variant="rounded"
                sx={{
                  bgcolor: alpha('#c2dd00', 0.1),
                  mr: 1,
                  borderRadius: 3,
                }}
              >
                <HomeWorkRoundedIcon
                  sx={{
                    verticalAlign: 'middle',
                    color: '#96b100',
                  }}
                />
              </Avatar>
            </ListItemIcon>

            <ListItemText
              primary={building.Name}
              secondary={
                building.Location?.Address
                  ? `${building.Location.Address}`
                  : 'Ingen adresse'
              }
              slotProps={{
                primary: {
                  variant: 'subtitle1',
                  color: 'textPrimary',
                  fontWeight: 500,
                },

                secondary: { variant: 'subtitle2' }
              }} />

            {building.BillingMeters.length === 0 && (
              <Typography variant="body2" color="error" sx={{ mr: 2 }}>
                {t('building.error.noBillingMeterFound', {
                  ns: 'buildingOptimization',
                })}
              </Typography>
            )}

            <ListItemIcon>
              <ArrowBackRoundedIcon
                className="arrow-icon"
                sx={{
                  transform: 'rotate(180deg)',
                  transition:
                    'transform 0.2s ease-in-out, color 0.2s ease-in-out',
                }}
              />
            </ListItemIcon>
          </ListItemButton>
        ))}
      </Stack>
    </>
  );
}
